import React, { createContext, useContext, useRef, useState, } from "react";
import { buildModuleUrl, ClockRange, defined, Ellipsoid, PostProcessStageLibrary, SceneMode, TileMapServiceImageryProvider, Viewer, PrimitiveCollection, JulianDate, ReferenceProperty, DebugModelMatrixPrimitive, } from "cesium";
import { ClockContext } from "./ClockContext";
export var Views;
(function (Views) {
    Views["VIEW2D"] = "VIEW2D";
    Views["VIEW3D"] = "VIEW3D";
    Views["VIEWCOLUMBUS"] = "VIEWCOLUMBUS";
})(Views || (Views = {}));
export const Widget3DContext = createContext({
    isViewerLoaded: null,
    viewer: null,
    viewerRef: null,
    updateViewerSimulationTime: null,
    addEntity: null,
    removeEntity: null,
    addPrimitive: null,
    removePrimitive: null,
    addDataSource: null,
    removeDataSource: null,
    addImageryProvider: null,
    removeImageryProvider: null,
    addClockEvent: null,
    getCameraFrustum: null,
    trackEntity: null,
    getEntityReferenceProperty: null,
    getSceneMode: null,
    debugPrimitiveAxes: null,
});
export const Widget3DProvider = ({ viewerRef, showDayAndNightEffect, initialView, fxaa, hdr, lensFlare, showUmbra, showPenumbra, maxMultiplierSpeed, onEntityPicked, isRealTime, children, }) => {
    var _a, _b, _c;
    const [isViewerLoaded, setIsViewerLoaded] = useState(false);
    const viewer = useRef(null);
    const { setClock } = useContext(ClockContext);
    (_c = (_b = (_a = viewer === null || viewer === void 0 ? void 0 : viewer.current) === null || _a === void 0 ? void 0 : _a.clock) === null || _b === void 0 ? void 0 : _b.onTick) === null || _c === void 0 ? void 0 : _c.addEventListener(() => {
        var _a;
        setClock((_a = viewer === null || viewer === void 0 ? void 0 : viewer.current) === null || _a === void 0 ? void 0 : _a.clock);
    });
    const isClockFirstUpdated = useRef(false);
    const primitiveCollection = useRef(null);
    const updateViewerSimulationTime = (startTime, stopTime, currentTime, multiplier, clockRange) => {
        var _a;
        if (((_a = viewer === null || viewer === void 0 ? void 0 : viewer.current) === null || _a === void 0 ? void 0 : _a.clock) && !isRealTime) {
            viewer.current.clock.currentTime = startTime;
            if (JulianDate.lessThan(startTime, viewer.current.clock.startTime)) {
                viewer.current.clock.startTime = startTime;
            }
            if (stopTime &&
                (!isClockFirstUpdated.current ||
                    JulianDate.lessThan(viewer.current.clock.stopTime, stopTime))) {
                viewer.current.clock.stopTime = stopTime;
            }
            viewer.current.clock.currentTime = currentTime ? currentTime : startTime;
            viewer.current.clock.multiplier =
                typeof multiplier === "undefined" ? 10.0 : multiplier;
            viewer.current.clock.clockRange = clockRange
                ? clockRange
                : ClockRange.LOOP_STOP;
            viewer.current.timeline.zoomTo(startTime, stopTime ? stopTime : JulianDate.addDays(startTime, 1, new JulianDate()));
        }
        isClockFirstUpdated.current = true;
        return null;
    };
    const addEntity = (entity) => {
        return viewer.current.entities.add(entity);
    };
    const removeEntity = (entity) => {
        if (!viewer.current.isDestroyed()) {
            viewer.current.entities.remove(entity);
        }
        return null;
    };
    const addPrimitive = (primitive) => {
        return primitiveCollection.current.add(primitive);
    };
    const removePrimitive = (primitive) => {
        if (!viewer.current.isDestroyed()) {
            primitiveCollection.current.remove(primitive);
        }
        return null;
    };
    const addDataSource = (dataSource) => {
        return viewer.current.dataSources.add(dataSource);
    };
    const removeDataSource = (dataSource) => {
        if (!viewer.current.isDestroyed()) {
            viewer.current.dataSources.remove(dataSource);
        }
        return null;
    };
    const addImageryProvider = (provider) => {
        return viewer.current.imageryLayers.addImageryProvider(provider);
    };
    const removeImageryProvider = (imageryLayer) => {
        if (!viewer.current.isDestroyed()) {
            viewer.current.imageryLayers.remove(imageryLayer);
        }
        return null;
    };
    const addClockEvent = (callbackEvent, t) => {
        if (t === 0 || typeof t === "undefined")
            return viewer.current.scene.preUpdate.addEventListener((scene, time) => {
                callbackEvent.getValue(time);
                scene.requestRender();
            });
        const clock = viewer.current.clock;
        let lastUpdated = clock.startTime;
        return viewer.current.scene.preUpdate.addEventListener((scene, time) => {
            var step = t * clock.multiplier;
            var dt = JulianDate.secondsDifference(time, lastUpdated);
            if (dt >= step || dt <= -step) {
                callbackEvent.getValue(time);
                scene.requestRender();
                lastUpdated = time;
            }
        });
    };
    const getCameraFrustum = () => {
        return viewer.current.camera.frustum;
    };
    const trackEntity = (entity) => {
        viewer.current.trackedEntity = entity;
    };
    const getEntityReferenceProperty = (entityId, reference) => {
        var _a;
        const stringRef = `${entityId}#${reference}`;
        const rp = ReferenceProperty.fromString((_a = viewer === null || viewer === void 0 ? void 0 : viewer.current) === null || _a === void 0 ? void 0 : _a.entities, stringRef);
        return rp;
    };
    const getSceneMode = () => {
        return viewer.current.scene.mode;
    };
    const debugPrimitiveAxes = (modelMatrix, length, width) => {
        return primitiveCollection.current.add(new DebugModelMatrixPrimitive({
            modelMatrix: modelMatrix,
            length: length ? length : 1000.0,
            width: width ? width : 2.0,
        }));
    };
    React.useEffect(() => {
        const sceneMode = !initialView
            ? SceneMode.SCENE3D
            : initialView === Views.VIEW2D
                ? SceneMode.SCENE2D
                : initialView === Views.VIEWCOLUMBUS
                    ? SceneMode.COLUMBUS_VIEW
                    : SceneMode.SCENE3D;
        const earthTexture = new TileMapServiceImageryProvider({
            url: buildModuleUrl("Assets/Textures/NaturalEarthII"),
        });
        viewer.current = new Viewer(viewerRef.current, {
            imageryProvider: earthTexture,
            baseLayerPicker: false,
            geocoder: false,
            fullscreenButton: true,
            shouldAnimate: true,
            shadows: true,
            requestRenderMode: true,
        });
        viewer.current.scene.mode = sceneMode;
        viewer.current.scene.postProcessStages.fxaa.enabled = fxaa;
        viewer.current.scene.highDynamicRange = hdr;
        viewer.current.scene.debugShowFramesPerSecond = true;
        if (maxMultiplierSpeed) {
            viewer.current.animation.viewModel.setShuttleRingTicks([
                Math.abs(maxMultiplierSpeed),
            ]);
        }
        if (showDayAndNightEffect) {
            const imageryLayers = viewer.current.imageryLayers;
            const nightLayer = imageryLayers.get(0);
            nightLayer.dayAlpha = 0;
            const dayLayer = imageryLayers.addImageryProvider(earthTexture);
            dayLayer.show = true;
            imageryLayers.lowerToBottom(dayLayer);
            viewer.current.scene.globe.enableLighting = true;
        }
        if (lensFlare) {
            const viewModel = {
                show: true,
                intensity: 0.5,
                distortion: 20.0,
                dispersion: 0.8,
                haloWidth: 0.4,
                dirtAmount: 1.5,
            };
            const lensFlare = viewer.current.scene.postProcessStages.add(PostProcessStageLibrary.createLensFlareStage());
            lensFlare.enabled = Boolean(viewModel.show);
            lensFlare.uniforms.intensity = Number(viewModel.intensity);
            lensFlare.uniforms.distortion = Number(viewModel.distortion);
            lensFlare.uniforms.ghostDispersal = Number(viewModel.dispersion);
            lensFlare.uniforms.haloWidth = Number(viewModel.haloWidth);
            lensFlare.uniforms.dirtAmount = Number(viewModel.dirtAmount);
            lensFlare.uniforms.earthRadius = Ellipsoid.WGS84.maximumRadius;
        }
        viewer.current.selectedEntityChanged.addEventListener((entity) => {
            if (defined(entity) && onEntityPicked) {
                onEntityPicked(entity.id);
            }
            else if (onEntityPicked) {
                onEntityPicked(null);
            }
        });
        primitiveCollection.current = viewer.current.scene.primitives.add(new PrimitiveCollection());
        setIsViewerLoaded(true);
        return () => {
            (viewer === null || viewer === void 0 ? void 0 : viewer.current) && viewer.current.entities.removeAll();
            (viewer === null || viewer === void 0 ? void 0 : viewer.current) && viewer.current.destroy();
        };
    }, []);
    const value = {
        isViewerLoaded,
        viewer: viewer.current,
        viewerRef,
        showUmbra,
        showPenumbra,
        updateViewerSimulationTime,
        addEntity,
        removeEntity,
        addPrimitive,
        removePrimitive,
        addDataSource,
        removeDataSource,
        addImageryProvider,
        removeImageryProvider,
        addClockEvent,
        getCameraFrustum,
        trackEntity,
        getEntityReferenceProperty,
        getSceneMode,
        debugPrimitiveAxes,
    };
    return (React.createElement(Widget3DContext.Provider, { value: value }, children));
};
export const useCesium = () => useContext(Widget3DContext);
//# sourceMappingURL=Widget3DContext.js.map